import { useState, type FormEvent } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { KeyRound } from "lucide-react";
import { AxiosError } from "axios";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Button,
  Label,
  PasswordInput,
} from "@lsf/ui";
import { useAuth } from "@/lib/auth/AuthProvider";

interface FieldErrors {
  current_password?: string[];
  new_password?: string[];
  new_password_confirmation?: string[];
}

export function ForcePasswordChangePage() {
  const { user, changePassword } = useAuth();
  const navigate = useNavigate();

  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState<FieldErrors>({});
  const [generalError, setGeneralError] = useState<string | null>(null);

  // Already changed — bounce to root
  if (user && !user.must_change_password) {
    return <Navigate to="/" replace />;
  }
  if (!user) {
    return <Navigate to="/login" replace />;
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setErrors({});
    setGeneralError(null);
    setSubmitting(true);

    try {
      await changePassword({
        current_password: currentPassword,
        new_password: newPassword,
        new_password_confirmation: confirmPassword,
      });
      navigate("/", { replace: true });
    } catch (err) {
      if (err instanceof AxiosError && err.response?.status === 422) {
        const data = err.response.data as { errors?: FieldErrors; message?: string };
        setErrors(data.errors ?? {});
        if (!data.errors) setGeneralError(data.message ?? "Could not update password");
      } else {
        setGeneralError("Could not update password. Try again.");
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <div className="w-full max-w-md">
        <Card className="shadow-xl">
          <CardHeader className="space-y-2">
            <div className="w-12 h-12 rounded-xl bg-accent/10 flex items-center justify-center">
              <KeyRound className="w-6 h-6 text-accent" />
            </div>
            <CardTitle className="text-2xl">Set a new password</CardTitle>
            <CardDescription>
              For security, you must change your initial password before continuing. Pick something
              you haven&apos;t used elsewhere.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4" noValidate>
              {generalError && (
                <div
                  role="alert"
                  className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive"
                >
                  {generalError}
                </div>
              )}

              <PasswordField
                id="current"
                label="Current password"
                autoComplete="current-password"
                value={currentPassword}
                onChange={setCurrentPassword}
                errors={errors.current_password}
              />
              <PasswordField
                id="new"
                label="New password"
                autoComplete="new-password"
                value={newPassword}
                onChange={setNewPassword}
                errors={errors.new_password}
                hint="At least 10 characters, mixed case, a number, and a symbol."
              />
              <PasswordField
                id="confirm"
                label="Confirm new password"
                autoComplete="new-password"
                value={confirmPassword}
                onChange={setConfirmPassword}
                errors={errors.new_password_confirmation}
              />

              <Button type="submit" disabled={submitting} className="w-full h-11 mt-2">
                {submitting ? "Updating…" : "Update password and continue"}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function PasswordField({
  id,
  label,
  value,
  onChange,
  errors,
  hint,
  autoComplete,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (v: string) => void;
  errors?: string[];
  hint?: string;
  autoComplete?: string;
}) {
  return (
    <div className="space-y-2">
      <Label htmlFor={`pw-${id}`} className="text-sm font-medium">
        {label}
      </Label>
      <PasswordInput
        id={`pw-${id}`}
        autoComplete={autoComplete}
        required
        className="h-11"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        aria-invalid={!!errors?.length}
      />
      {hint && !errors?.length && <p className="text-xs text-muted-foreground">{hint}</p>}
      {errors?.map((msg) => (
        <p key={msg} className="text-xs text-destructive">
          {msg}
        </p>
      ))}
    </div>
  );
}
